/**
 * CV deployment progress, derived from Lebron's cv update request/response rows.
 *
 * A deployment is one `cv_update.request` message. Its `request_id` is the
 * hedwig message id, which every base station echoes back on each response, so
 * it is the deployment id and the only safe join between a request and its
 * outcomes.
 *
 * **Responses are events, not state.** A base station reports `IN_PROGRESS`
 * when it starts and a terminal status when it finishes, and each is its own
 * row — nothing is ever updated in place. So anything asking "where is store X
 * now" has to reduce that store's rows to its latest event first; counting rows
 * double-counts every store that has already started.
 */
import type {
  CvUpdateRequestSchema,
  CvUpdateResponseSchema,
  StoreInventorySchema,
} from "@labeling-tools/api/lebron/generated/types.gen";

export type { CvUpdateRequestSchema, CvUpdateResponseSchema };

/**
 * The statuses a base station reports, in lifecycle order.
 *
 * `status` is typed as a bare `string` in the OpenAPI spec (Django
 * `TextChoices` serialises as one), so an unrecognised value is possible and is
 * treated as terminal rather than dropped — see `isTerminal`.
 */
export const IN_PROGRESS = "IN_PROGRESS";
export const SUCCEEDED = "SUCCEEDED";
export const FAILED = "FAILED";
export const SKIPPED = "SKIPPED";

export const TERMINAL_STATUSES = [SUCCEEDED, FAILED, SKIPPED] as const;

/**
 * Whether a status means the store is done.
 *
 * Only `IN_PROGRESS` is non-terminal. An unknown status — a lifecycle stage
 * added to Lebron after this build — counts as terminal, so a deployment that
 * has actually finished is never shown as permanently running.
 */
export function isTerminal(status: string): boolean {
  return status !== IN_PROGRESS;
}

/** Mantine colour per status, shared by the bar, the badges and the legend. */
export function statusColor(status: string): string {
  switch (status) {
    case SUCCEEDED:
      return "green";
    case FAILED:
      return "red";
    case SKIPPED:
      return "yellow";
    case IN_PROGRESS:
      return "blue";
    default:
      return "gray";
  }
}

/** `"IN_PROGRESS"` → `"In progress"`. */
export function statusLabel(status: string): string {
  const words = status.replace(/_/g, " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The stores a blast deploy addresses.
 *
 * Mirrors `plugins/inventory/base_station_inventory.py` in
 * augmodo-ansible-playbook, which builds the `base_stations` group from
 * `GET /v1/inventory/stores` by taking every store that is `active` **and**
 * carries a non-empty `tags` object — an untagged store is skipped there
 * because the playbook needs `augmodo_store_name`/`_client`/`_id` from it.
 * Keep the two predicates in step: this is the denominator a fleet blast is
 * measured against, and widening it here would make a complete deploy look
 * stalled.
 *
 * Keyed by `site_id`, since that is what a response's `sid` carries.
 */
export function fleetSids(
  inventory: StoreInventorySchema[] | undefined,
): Set<string> {
  const sids = new Set<string>();
  for (const store of inventory ?? []) {
    if (!store.active) continue;
    if (Object.keys(store.tags ?? {}).length === 0) continue;
    sids.add(store.site_id);
  }
  return sids;
}

/** A store's current position in one deployment, reduced from its events. */
export interface StoreProgress {
  sid: string;
  name: string | null;
  /** Null when the store is in the fleet but has not reported at all. */
  status: string | null;
  /** When the store first reported anything for this deployment. */
  startedAt: string | null;
  /** When it reported its latest event. */
  updatedAt: string | null;
  /** Milliseconds from first to latest event, or null if either is unusable. */
  elapsedMs: number | null;
}

function eventTime(response: CvUpdateResponseSchema): number {
  const value = Date.parse(response.created_at);
  return Number.isNaN(value) ? 0 : value;
}

/**
 * Reduce a deployment's response rows to one entry per store, newest first.
 *
 * Ordering by `created_at` alone is not quite enough: a fast store can record
 * `IN_PROGRESS` and its outcome inside the same clock tick, and Lebron's
 * ordering between two rows of the same timestamp is not defined. Ties
 * therefore resolve to the terminal row, because `IN_PROGRESS` is only ever
 * emitted *before* an outcome — never after one.
 */
export function latestEventPerStore(
  responses: CvUpdateResponseSchema[],
): StoreProgress[] {
  const bySid = new Map<
    string,
    { latest: CvUpdateResponseSchema; first: CvUpdateResponseSchema }
  >();

  for (const response of responses) {
    const current = bySid.get(response.sid);
    if (!current) {
      bySid.set(response.sid, { latest: response, first: response });
      continue;
    }

    const time = eventTime(response);
    const latestTime = eventTime(current.latest);
    const isNewer =
      time > latestTime ||
      (time === latestTime &&
        isTerminal(response.status) &&
        !isTerminal(current.latest.status));
    if (isNewer) current.latest = response;

    if (time < eventTime(current.first)) current.first = response;
  }

  return [...bySid.values()]
    .map(({ latest, first }) => {
      const startedAt = first.created_at;
      const updatedAt = latest.created_at;
      const span = Date.parse(updatedAt) - Date.parse(startedAt);
      return {
        sid: latest.sid,
        name: latest.store?.name ?? null,
        status: latest.status,
        startedAt,
        updatedAt,
        elapsedMs: Number.isFinite(span) && span >= 0 ? span : null,
      };
    })
    .sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""));
}

export interface DeploymentProgress {
  /** One row per store, including fleet members that have not reported. */
  stores: StoreProgress[];
  /**
   * How many stores this deployment should eventually reach, or null when that
   * cannot be known (a blast with no store inventory to measure against).
   */
  expected: number | null;
  /** Stores that have reported at least once. */
  reported: number;
  /** Stores whose latest event is terminal. */
  finished: number;
  counts: {
    inProgress: number;
    succeeded: number;
    failed: number;
    skipped: number;
    other: number;
  };
  /** Fleet members with no response yet. Null when `expected` is unknown. */
  silent: number | null;
  /** The newest event seen for this deployment, for the quiet-period check. */
  lastEventAt: string | null;
  /** `finished / expected` as 0–1, or null when `expected` is unknown. */
  fraction: number | null;
  /**
   * True once every store that is going to answer has answered. For a blast
   * that means every fleet member reported a terminal status — stores whose
   * base station is offline never do, so a blast can legitimately sit below
   * 100% forever. `silent` is what says why.
   */
  isComplete: boolean;
}

/**
 * Fold a deployment's requests and responses into the numbers the bar shows.
 *
 * `fleet` is only consulted for a blast. A targeted deploy addresses the one
 * store named on its request, so its denominator is exactly 1 and does not
 * depend on the inventory being loaded.
 */
export function deploymentProgress(
  request: CvUpdateRequestSchema | undefined,
  responses: CvUpdateResponseSchema[],
  fleet: Set<string>,
): DeploymentProgress {
  const reportedStores = latestEventPerStore(responses);
  const seen = new Set(reportedStores.map((store) => store.sid));

  // A blast is addressed to the whole fleet, so stores that have not reported
  // are part of the picture: they are the ones still to come, or the ones whose
  // base station was never reachable. Listing them is the only way the table
  // can show a store as missing rather than silently omitting it.
  const targetSids = request?.blast
    ? fleet
    : new Set(request?.sid ? [request.sid] : []);

  const silentStores: StoreProgress[] = [...targetSids]
    .filter((sid) => !seen.has(sid))
    .map((sid) => ({
      sid,
      name: null,
      status: null,
      startedAt: null,
      updatedAt: null,
      elapsedMs: null,
    }));

  const stores = [...reportedStores, ...silentStores];

  const counts = {
    inProgress: 0,
    succeeded: 0,
    failed: 0,
    skipped: 0,
    other: 0,
  };
  for (const store of reportedStores) {
    switch (store.status) {
      case IN_PROGRESS:
        counts.inProgress++;
        break;
      case SUCCEEDED:
        counts.succeeded++;
        break;
      case FAILED:
        counts.failed++;
        break;
      case SKIPPED:
        counts.skipped++;
        break;
      default:
        counts.other++;
    }
  }

  const finished = reportedStores.filter(
    (store) => store.status !== null && isTerminal(store.status),
  ).length;

  // A blast with no inventory loaded has no honest denominator, so it reports
  // null rather than quietly falling back to the stores seen so far — that
  // would read as "99% done" from the first response onwards.
  let expected: number | null;
  if (request?.blast) {
    expected = fleet.size > 0 ? fleet.size : null;
  } else if (request?.sid) {
    expected = 1;
  } else {
    expected = null;
  }

  return {
    stores,
    expected,
    reported: reportedStores.length,
    finished,
    counts,
    // reportedStores is sorted newest-first by latestEventPerStore.
    lastEventAt: reportedStores[0]?.updatedAt ?? null,
    silent:
      expected === null ? null : Math.max(0, expected - reportedStores.length),
    fraction: expected === null ? null : Math.min(1, finished / expected),
    isComplete: expected !== null && finished >= expected,
  };
}

/**
 * How long a deployment may go without a new event before polling gives up.
 *
 * deploysettings' own `await-canary` action waits 30 minutes for one store,
 * with the note that stores have taken over 20 under slow outbound conditions,
 * so anything shorter would stop watching a deploy that is merely slow.
 */
export const DEPLOYMENT_QUIET_PERIOD_MS = 30 * 60 * 1000;

/**
 * Whether a deployment is still worth polling.
 *
 * Completion alone is not a usable stop condition for a blast: a base station
 * that is offline never answers, so `isComplete` can stay false forever and the
 * page would poll a week-old deploy indefinitely. Silence is the other half of
 * the rule — once nothing new has arrived for `DEPLOYMENT_QUIET_PERIOD_MS`,
 * the stores that were going to answer have.
 */
export function isDeploymentLive(
  request: CvUpdateRequestSchema | undefined,
  progress: DeploymentProgress,
  now: number = Date.now(),
): boolean {
  if (!request) return false;
  if (progress.isComplete) return false;

  // Before the first response arrives the request's own age is all there is to
  // go on, which is also what keeps a just-dispatched deploy polling.
  const last = Date.parse(progress.lastEventAt ?? request.created_at);
  if (Number.isNaN(last)) return false;
  return now - last < DEPLOYMENT_QUIET_PERIOD_MS;
}

/** How a deployment reads in the picker: scope, versions, release, age. */
export function deploymentLabel(request: CvUpdateRequestSchema): string {
  const scope = request.blast
    ? "Fleet"
    : (request.store?.name ?? request.sid ?? "One store");
  const versions = `${request.cv_pipeline_image_tag} · ${request.triton_model_repo_version}`;
  const parts = [`${scope} — ${versions}`];
  if (request.ansible_ref) parts.push(request.ansible_ref);
  if (request.author) parts.push(`by ${request.author}`);
  return parts.join(" · ");
}

/** `"2026-10-19T09:00:00Z"` → `"4m ago"`. Coarse on purpose. */
export function relativeTime(
  value: string | null | undefined,
  now: number = Date.now(),
): string {
  if (!value) return "—";
  const then = Date.parse(value);
  if (Number.isNaN(then)) return value;

  const seconds = Math.round((now - then) / 1000);
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** `93000` → `"1m 33s"`. */
export function formatElapsed(ms: number | null): string {
  if (ms === null) return "—";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

/** Distinct, sorted, non-empty values of one request field, for a filter. */
export function distinctValues(
  requests: CvUpdateRequestSchema[],
  pick: (request: CvUpdateRequestSchema) => string | null | undefined,
): string[] {
  const values = new Set<string>();
  for (const request of requests) {
    const value = pick(request);
    if (value) values.add(value);
  }
  return [...values].sort();
}

export type DeploymentScope = "blast" | "store";

export interface DeploymentFilters {
  scope: DeploymentScope | null;
  author: string | null;
  ansibleRef: string | null;
  imageTag: string | null;
}

export const NO_DEPLOYMENT_FILTERS: DeploymentFilters = {
  scope: null,
  author: null,
  ansibleRef: null,
  imageTag: null,
};

/**
 * Narrow the deployment list to what the picker should offer.
 *
 * Requests with no `request_id` are dropped outright: nothing can be joined to
 * them, so selecting one would show an empty deployment with no way to tell
 * that from a deploy nobody has answered yet. They predate the base stations
 * echoing the id back.
 */
export function filterDeployments(
  requests: CvUpdateRequestSchema[],
  filters: DeploymentFilters,
): CvUpdateRequestSchema[] {
  return requests.filter((request) => {
    if (!request.request_id) return false;
    if (filters.scope === "blast" && !request.blast) return false;
    if (filters.scope === "store" && request.blast) return false;
    if (filters.author && request.author !== filters.author) return false;
    if (filters.ansibleRef && request.ansible_ref !== filters.ansibleRef)
      return false;
    if (filters.imageTag && request.cv_pipeline_image_tag !== filters.imageTag)
      return false;
    return true;
  });
}
